import { ApolloCache } from "@apollo/client";
import { GraphQLFormattedError } from "graphql";

import {
  NotesCreateNoteMutation,
  NotesDeleteNoteMutation,
  NotesListNotesByPlayerIdDocument,
  NotesListNotesByPlayerIdQuery,
} from "@/generated/graphql";

/**
 * Helper type to infer the mutation data shape based on operation type
 * @remarks
 * Uses conditional types to map "create" to NotesCreateNoteMutation and
 * "delete" to NotesDeleteNoteMutation for type-safe cache updates.
 */
type DataType<T> = T extends "create"
  ? NotesCreateNoteMutation
  : NotesDeleteNoteMutation;

/**
 * Updates Apollo cache after create or delete note mutation
 *
 * Optimistically updates the cache to add or remove notes from the list
 * without refetching. Only updates cache if existing notes are present,
 * data is valid, and no errors occurred.
 * @template T - Operation type ("create" or "delete")
 * @param type - The type of operation being performed
 * @param cache - Apollo cache instance to update
 * @param data - Mutation response data containing the created/deleted note
 * @param errors - GraphQL errors from the mutation (if any)
 * @param playerId - ID of the player whose notes list should be updated
 */
const handleCreateOrDeleteNote = <T extends "create" | "delete">(
  type: T,
  cache: ApolloCache<unknown>,
  data: DataType<T> | null | undefined,
  errors: readonly GraphQLFormattedError[] | undefined,
  playerId: string
): void => {
  const existingNotes: NotesListNotesByPlayerIdQuery | null = cache.readQuery({
    query: NotesListNotesByPlayerIdDocument,
    variables: { playerId },
  });
  const dataAction =
    type === "create"
      ? (data as NotesCreateNoteMutation)?.createNote
      : (data as NotesDeleteNoteMutation)?.deleteNote;
  if (!!existingNotes && dataAction && !errors) {
    cache.writeQuery({
      query: NotesListNotesByPlayerIdDocument,
      variables: { playerId },
      data:
        type === "create"
          ? {
              listNotesByPlayerId: {
                ...existingNotes.listNotesByPlayerId,
                edges: [
                  {
                    __typename: "NoteEdge",
                    cursor: `temp-cursor-${dataAction.id}`,
                    node: dataAction,
                  },
                  ...existingNotes.listNotesByPlayerId.edges,
                ],
              },
            }
          : {
              listNotesByPlayerId: {
                ...existingNotes.listNotesByPlayerId,
                edges: existingNotes.listNotesByPlayerId.edges.filter(
                  item => item.node.id !== dataAction.id
                ),
              },
            },
    });
  }
};

export { handleCreateOrDeleteNote };
